/**
 * Case Notes Analysis Component
 * Displays NLP analysis results from case notes including SDOH factors, risk indicators, sentiment
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  FileText,
  AlertTriangle,
  Heart,
  TrendingUp,
  TrendingDown,
  Languages,
  Loader2,
  RefreshCw,
  Users,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { getWeaveinApiUrl } from '@/src/config';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';

interface Member {
  member_id: number;
  first_name: string;
  last_name: string;
  risk_level?: string;
}

interface CaseNoteAnalysis {
  case_note_id: number;
  member_id: number;
  cbo_id: number;
  extracted_sdoh_factors: Record<string, string[]>;
  extracted_risk_factors: Record<string, string[]>;
  extracted_clinical_indicators: {
    conditions: string[];
    symptoms: string[];
    medications: string[];
    procedures: string[];
  };
  sentiment_score: number;
  urgency_score: number;
  key_phrases: string[];
  topics: string[];
  detected_language: string;
  language_confidence: number;
  confidence_score: number;
  analyzed_at: string;
}

interface CaseNotesAnalysisProps {
  memberId: number;
  cboId?: number;
  caseNoteId?: number;
}

export const CaseNotesAnalysis: React.FC<CaseNotesAnalysisProps> = ({
  memberId: propMemberId,
  cboId,
  caseNoteId,
}) => {
  const { token: contextToken } = useAuth();
  const { fmt } = useFormattedDate();
  const [analyses, setAnalyses] = useState<CaseNoteAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState<CaseNoteAnalysis | null>(null);

  // Member selection state (for standalone usage)
  const [members, setMembers] = useState<Member[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState<number>(propMemberId || 0);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Use prop memberId if provided, otherwise use selected
  const memberId = propMemberId && propMemberId > 0 ? propMemberId : selectedMemberId;

  // Helper function to get auth token
  const getAuthToken = (): string | null => {
    if (typeof window === 'undefined') return null;
    return contextToken || localStorage.getItem('auth_token');
  };

  // Fetch members for dropdown when no memberId is provided
  useEffect(() => {
    const authToken = getAuthToken();
    if ((!propMemberId || propMemberId <= 0) && authToken && cboId) {
      fetchMembers();
    }
  }, [propMemberId, cboId, contextToken]);

  const fetchMembers = async () => {
    const authToken = getAuthToken();
    if (!authToken) return;

    try {
      setLoadingMembers(true);
      const activeCboId = cboId || localStorage.getItem('active_cbo_id') || '1';
      const response = await authFetch(`${getWeaveinApiUrl()}/api/retention/all-members/${activeCboId}?page=1&page_size=500`);
      if (response.ok) {
        const data = await response.json();
        setMembers(data.members || data || []);
      }
    } catch (err) {
      console.error('Error fetching members:', err);
    } finally {
      setLoadingMembers(false);
    }
  };

  useEffect(() => {
    const authToken = getAuthToken();
    if (memberId && memberId > 0 && authToken) {
      fetchAnalyses();
    } else if (!memberId || memberId <= 0) {
      setLoading(false);
      // Don't set error - we'll show member selector instead
    } else if (!authToken) {
      setLoading(false);
      setError('Authentication required');
    }
  }, [memberId, cboId, caseNoteId, contextToken]);

  const fetchAnalyses = async () => {
    if (!memberId || memberId <= 0) {
      setError('Invalid member ID');
      setLoading(false);
      return;
    }

    const authToken = getAuthToken();
    if (!authToken) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let url = `${getWeaveinApiUrl()}/api/case-notes/analysis`;
      const params = new URLSearchParams();
      params.append('member_id', memberId.toString());
      if (cboId) params.append('cbo_id', cboId.toString());
      if (caseNoteId) params.append('case_note_id', caseNoteId.toString());
      url += `?${params.toString()}`;
      if (!authToken) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error('Failed to fetch case notes analysis');
      }

      const data = await response.json();
      setAnalyses(Array.isArray(data) ? data : [data]);
      if (data.length > 0) {
        setSelectedAnalysis(data[0]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analysis');
      console.error('Error fetching case notes analysis:', err);
    } finally {
      setLoading(false);
    }
  };

  const getSentimentColor = (score: number) => {
    if (score > 0.3) return 'text-green-600';
    if (score < -0.3) return 'text-red-600';
    return 'text-yellow-600';
  };

  const getSentimentLabel = (score: number) => {
    if (score > 0.3) return 'Positive';
    if (score < -0.3) return 'Negative';
    return 'Neutral';
  };

  const getUrgencyColor = (score: number) => {
    if (score >= 70) return 'text-red-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-green-600';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error && error !== 'Please select a member to view case notes analysis') {
    return (
      <Card>
        <CardContent>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Show member selector when no member is selected
  if (!memberId || memberId <= 0) {
    return (
      <Card>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-5 w-5 text-muted-foreground" />
              <Label htmlFor="member-select" className="text-base font-medium">
                Select a Member
              </Label>
            </div>

            {loadingMembers ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading members...</span>
              </div>
            ) : members.length > 0 ? (
              <Select
                value={selectedMemberId > 0 ? selectedMemberId.toString() : ''}
                onValueChange={(value) => setSelectedMemberId(parseInt(value))}
              >
                <SelectTrigger id="member-select" className="w-full max-w-md">
                  <SelectValue placeholder="Choose a member to view case notes analysis" />
                </SelectTrigger>
                <SelectContent>
                  {members.map((member) => (
                    <SelectItem key={member.member_id} value={member.member_id.toString()}>
                      {member.first_name} {member.last_name}
                      {member.risk_level && (
                        <span className="ml-2 text-muted-foreground">({member.risk_level})</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-muted-foreground">No members found. Please ensure members are enrolled in your organization.</p>
            )}

            <div className="mt-6 p-4 bg-muted rounded-lg">
              <h4 className="font-medium mb-2">About Case Notes Analysis</h4>
              <p className="text-sm text-muted-foreground">
                Case notes are automatically analyzed using NLP to extract:
              </p>
              <ul className="list-disc list-inside text-sm text-muted-foreground mt-2 space-y-1">
                <li>SDOH factors (housing, food, transportation, etc.)</li>
                <li>Risk indicators and clinical concerns</li>
                <li>Sentiment and urgency scores</li>
                <li>Key phrases and topics for ML predictions</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (analyses.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center h-64 text-center">
          <FileText className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No case notes analysis available for this member</p>
          <p className="text-sm text-muted-foreground mt-2">
            Case notes will be automatically analyzed for SDOH factors and risk indicators
          </p>
          <Button variant="outline" className="mt-4" onClick={() => setSelectedMemberId(0)}>
            Select Different Member
          </Button>
        </CardContent>
      </Card>
    );
  }

  const analysis = selectedAnalysis || analyses[0];

  // Helper function to safely format numbers
  const safeToFixed = (value: number | undefined | null, decimals: number = 0): string => {
    if (value === undefined || value === null || isNaN(value)) return '0';
    return value.toFixed(decimals);
  };

  const safeValue = (value: number | undefined | null): number => {
    if (value === undefined || value === null || isNaN(value)) return 0;
    return value;
  };

  return (
    <Card>
      {/* Toolbar (page title rendered by the shell chrome) */}
      <CardHeader>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={fetchAnalyses}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {analyses.length > 1 && (
          <div className="mb-4">
            <Label>Select Analysis</Label>
            <Select
              value={analysis.case_note_id.toString()}
              onValueChange={(value) => {
                const selected = analyses.find((a) => a.case_note_id.toString() === value);
                setSelectedAnalysis(selected || null);
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {analyses.map((a) => (
                  <SelectItem key={a.case_note_id} value={a.case_note_id.toString()}>
                    Note #{a.case_note_id} - {fmt.short(a.analyzed_at)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <Tabs defaultValue="overview" className="w-full">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="sdoh">SDOH Factors</TabsTrigger>
            <TabsTrigger value="risks">Risk Indicators</TabsTrigger>
            <TabsTrigger value="clinical">Clinical</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Sentiment</p>
                <div className="flex items-center gap-2">
                  <p className={`text-2xl font-bold ${getSentimentColor(safeValue(analysis.sentiment_score))}`}>
                    {getSentimentLabel(safeValue(analysis.sentiment_score))}
                  </p>
                  {safeValue(analysis.sentiment_score) > 0.3 ? (
                    <TrendingUp className="h-5 w-5 text-green-600" />
                  ) : safeValue(analysis.sentiment_score) < -0.3 ? (
                    <TrendingDown className="h-5 w-5 text-red-600" />
                  ) : null}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Score: {safeToFixed(analysis.sentiment_score, 2)}
                </p>
              </div>

              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Urgency</p>
                <div className="flex items-center gap-2">
                  <p className={`text-2xl font-bold ${getUrgencyColor(safeValue(analysis.urgency_score))}`}>
                    {safeValue(analysis.urgency_score) >= 70
                      ? 'High'
                      : safeValue(analysis.urgency_score) >= 50
                      ? 'Medium'
                      : 'Low'}
                  </p>
                  {safeValue(analysis.urgency_score) >= 70 && (
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                  )}
                </div>
                <Progress value={safeValue(analysis.urgency_score)} className="mt-2" />
              </div>
            </div>

            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground mb-2">Detected Language</p>
              <div className="flex items-center gap-2">
                <Languages className="h-4 w-4" />
                <Badge variant="outline">{(analysis.detected_language || 'en').toUpperCase()}</Badge>
                <span className="text-sm text-muted-foreground">
                  Confidence: {safeToFixed(safeValue(analysis.language_confidence) * 100)}%
                </span>
              </div>
            </div>

            <div>
              <p className="text-sm font-medium mb-2">Key Topics</p>
              <div className="flex flex-wrap gap-2">
                {analysis.topics.map((topic) => (
                  <Badge key={topic} variant="secondary">
                    {topic}
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm font-medium mb-2">Key Phrases</p>
              <div className="flex flex-wrap gap-2">
                {analysis.key_phrases.slice(0, 10).map((phrase, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs">
                    {phrase}
                  </Badge>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="sdoh" className="space-y-4">
            {Object.keys(analysis.extracted_sdoh_factors).length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No SDOH factors detected</p>
            ) : (
              Object.entries(analysis.extracted_sdoh_factors).map(([category, factors]) => (
                <div key={category} className="p-4 border rounded-lg">
                  <p className="font-medium mb-2 capitalize">{category.replace('_', ' ')}</p>
                  <div className="flex flex-wrap gap-2">
                    {factors.map((factor, idx) => (
                      <Badge key={idx} variant="secondary">
                        {factor}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))
            )}
          </TabsContent>

          <TabsContent value="risks" className="space-y-4">
            {Object.keys(analysis.extracted_risk_factors).length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No risk factors detected</p>
            ) : (
              Object.entries(analysis.extracted_risk_factors).map(([category, factors]) => (
                <div key={category} className="p-4 border rounded-lg">
                  <p className="font-medium mb-2 capitalize flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    {category.replace('_', ' ')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {factors.map((factor, idx) => (
                      <Badge key={idx} variant="destructive">
                        {factor}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))
            )}
          </TabsContent>

          <TabsContent value="clinical" className="space-y-4">
            <div>
              <p className="font-medium mb-2">Chronic Conditions</p>
              {analysis.extracted_clinical_indicators.conditions.length === 0 ? (
                <p className="text-muted-foreground">None detected</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {analysis.extracted_clinical_indicators.conditions.map((condition, idx) => (
                    <Badge key={idx} variant="outline">
                      {condition}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div>
              <p className="font-medium mb-2">Medications</p>
              {analysis.extracted_clinical_indicators.medications.length === 0 ? (
                <p className="text-muted-foreground">None detected</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {analysis.extracted_clinical_indicators.medications.map((med, idx) => (
                    <Badge key={idx} variant="secondary">
                      {med}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-4 pt-4 border-t">
          <p className="text-xs text-muted-foreground">
            Analyzed: {fmt.dateTime(analysis.analyzed_at)} • Confidence:{' '}
            {safeToFixed(analysis.confidence_score)}%
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default CaseNotesAnalysis;

